/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React,{useState,useEffect} from 'react';
import {openTemplatedPrintWindow} from '../utils/printUtils';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';

const SPECIMEN_TYPES=['Blood','Urine','Pus / Wound Swab','Sputum','CSF','Stool','Throat Swab','Nasal Swab','Tracheal Aspirate','BAL','Pleural Fluid','Ascitic Fluid','Tissue Biopsy','Other'];
const GROWTH_COLORS:Record<string,string>={NO_GROWTH:'bg-green-100 text-green-700',GROWTH_PRESENT:'bg-red-100 text-red-700',CONTAMINATED:'bg-yellow-100 text-yellow-700',AWAITING:'bg-slate-100 text-slate-500',MIXED_GROWTH:'bg-orange-100 text-orange-700'};
const SENSITIVITY=['S','I','R','NT'];
const SEN_COLORS:Record<string,string>={S:'bg-green-500',I:'bg-yellow-500',R:'bg-red-500',NT:'bg-slate-200'};
const SEN_LABELS:Record<string,string>={S:'Sensitive',I:'Intermediate',R:'Resistant',NT:'Not Tested'};

const ANTIBIOTIC_GROUPS=[
  {group:'Penicillins',drugs:['Amoxicillin','Ampicillin','Amoxicillin-Clavulanate','Piperacillin-Tazobactam']},
  {group:'Cephalosporins',drugs:['Cefazolin (1st)','Cefuroxime (2nd)','Ceftriaxone (3rd)','Cefepime (4th)','Ceftazidime']},
  {group:'Carbapenems',drugs:['Imipenem','Meropenem','Ertapenem']},
  {group:'Fluoroquinolones',drugs:['Ciprofloxacin','Levofloxacin','Moxifloxacin']},
  {group:'Aminoglycosides',drugs:['Gentamicin','Amikacin','Tobramycin']},
  {group:'Macrolides & Tetracyclines',drugs:['Azithromycin','Doxycycline','Tigecycline']},
  {group:'Others',drugs:['Trimethoprim-Sulfamethoxazole','Metronidazole','Vancomycin','Linezolid','Colistin','Chloramphenicol']},
];

const REPORT_TEMPLATES=[
  {id:'CBC',name:'Complete Blood Count (CBC)',category:'Haematology',params:['HGB','TLC','PLT','RBC','PCV','MCV','MCH','MCHC','NEUT','LYMPH','MONO','EOS','BASO','ESR']},
  {id:'LFT',name:'Liver Function Test (LFT)',category:'Liver Function',params:['TBIL','DBIL','IBIL','SGOT','SGPT','ALP','TPROT','ALB','GLOB','AGRATIO']},
  {id:'KFT',name:'Kidney Function Test (KFT)',category:'Renal Function',params:['UREA','CREAT','UA','NA','K','CL','EGFR']},
  {id:'LIPID',name:'Lipid Profile',category:'Lipid Profile',params:['CHOL','TG','HDL','LDL','VLDL']},
  {id:'THYROID',name:'Thyroid Function Test (TFT)',category:'Thyroid',params:['TSH','T3','T4','FT3','FT4']},
  {id:'GLUCOSE',name:'Blood Sugar Panel',category:'Biochemistry',params:['FBS','PPBS','RBS','HBA1C']},
  {id:'ELYTE',name:'Serum Electrolytes',category:'Biochemistry',params:['NA','K','CL','CA','PHOS','MG']},
  {id:'HIV',name:'HIV / HBsAg / HCV Screen',category:'Serology',params:['HIV','HBsAg','HCV','VDRL']},
  {id:'COAG',name:'Coagulation Profile',category:'Coagulation',params:['PT','INR','APTT']},
  {id:'DENGUE',name:'Dengue Serology',category:'Serology',params:['DENGNS1','DENGIGM']},
  {id:'FEVER',name:'Fever Panel (MP + Widal + Dengue)',category:'Serology',params:['MP','WIDAL','DENGNS1','DENGIGM']},
  {id:'URINE',name:'Urine Routine & Microscopy',category:'Urinalysis',params:['UCOLOR','UAPPEAR','UPH','USG','UPROT','UGLUC','UPUS','URBC']},
  {id:'ANTENATAL',name:'Antenatal Panel',category:'Mixed',params:['HGB','FBS','HIV','HBsAg','HCV','VDRL','TSH','VIT_D']},
  {id:'PREOP',name:'Pre-Operative Panel',category:'Mixed',params:['HGB','TLC','PLT','PT','INR','APTT','FBS','UREA','CREAT','HIV','HBsAg']},
];

export default function LabCultureTemplatePage(){
  const [section,setSection]=useState<'culture'|'templates'>('culture');
  const [cultures,setCultures]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [showForm,setShowForm]=useState(false);
  const [culturePatient,setCulturePatient]=useState<any>(null);
  const [selectedCulture,setSelectedCulture]=useState<any>(null);
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const defaultCForm={specimenType:'Blood',collectionDate:new Date().toISOString().split('T')[0],growthStatus:'AWAITING',gramStain:'',colonyCount:'',incubationHours:'48',organismIdentified:'',finalReport:'',clinicalNotes:''};
  const [cForm,setCForm]=useState<any>({...defaultCForm});
  const [abxRows,setAbxRows]=useState<any[]>([]);
  const [showAllAbx,setShowAllAbx]=useState(false);

  const [organisms,setOrganisms]=useState<any[]>([]);
  const [antibioticsMaster,setAntibioticsMaster]=useState<any[]>([]);

  const loadData=async()=>{
    setLoading(true);
    try{
      const [c,org,abx]=await Promise.all([
        api.getLabCultures?.().catch(()=>({data:[]})),
        api.getLabOrganisms?.().catch(()=>({data:[]})),
        api.getLabAntibiotics?.().catch(()=>({data:[]})),
      ]);
      setCultures(Array.isArray(c?.data)?c.data:[]);
      setOrganisms(Array.isArray(org?.data)?org.data:[]);
      setAntibioticsMaster(Array.isArray(abx?.data)?abx.data:[]);
    }catch{}
    setLoading(false);
  };
  useEffect(()=>{loadData();},[]);

  const initAbx=()=>{
    if(antibioticsMaster.length>0){
      // Use antibiotic master grouped by class
      setAbxRows(antibioticsMaster.map((a:any)=>({antibiotic:a.antibiotic_name,group:a.antibiotic_class||'Other',mic:'',sensitivity:'NT',who:a.who_classification})));
    } else {
      setAbxRows(ANTIBIOTIC_GROUPS.flatMap(g=>g.drugs.map(d=>({antibiotic:d,group:g.group,mic:'',sensitivity:'NT'}))));
    }
  };

  const saveCulture=async()=>{
    setSaving(true);
    try{
      await api.saveLabCulture?.({...cForm,antibiotics:abxRows.filter(a=>a.sensitivity!=='NT'||a.mic)});
      notify('✅ Culture report saved!');setShowForm(false);await loadData();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const printCulture=async(c:any)=>{
    const abx=(c.antibiotic_results||c.antibiotics||[]);
    const organisms_list=(c.organisms||[]);
    const contentHtml=`
      <div class="section">
        <div class="row">
          <div class="field"><div class="lbl">Patient</div><div class="val">${c.patient_first_name||''} ${c.patient_last_name||''} (${c.uhid||'—'})</div></div>
          <div class="field"><div class="lbl">Specimen Type</div><div class="val">${c.specimen_type||''}</div></div>
        </div>
        <div class="row">
          <div class="field"><div class="lbl">Collection Date</div><div class="val">${c.collection_date||''}</div></div>
          <div class="field"><div class="lbl">Incubation</div><div class="val">${c.incubation_days||c.incubation_hours||'48'} hours</div></div>
        </div>
      </div>
      <h3 class="section-title">Growth Status</h3>
      <div style="background:${(c.growth_status||c.growthStatus)==='NO_GROWTH'?'#dcfce7':'#fee2e2'};border-radius:6px;padding:10px 14px;font-weight:bold;font-size:14px;">
        ${(c.growth_status||c.growthStatus)==='NO_GROWTH'?'✅ NO SIGNIFICANT GROWTH':'⚠️ GROWTH PRESENT — '+(c.organism_identified||c.organismIdentified||'See below')}
      </div>
      ${c.gram_stain||c.gramStain?`<h3 class="section-title">Gram Stain</h3><p class="data-val">${c.gram_stain||c.gramStain}</p>`:''}
      ${c.colony_count||c.colonyCount?`<h3 class="section-title">Colony Count</h3><p class="data-val">${c.colony_count||c.colonyCount}</p>`:''}
      ${abx.length>0?`<h3 class="section-title">Antibiotic Sensitivity</h3>
        <table class="data-table"><thead><tr><th>Antibiotic</th><th>MIC</th><th>Result</th></tr></thead>
        <tbody>${abx.filter((a:any)=>a.sensitivity!=='NT'&&a.sensitivity!=='NOT_TESTED').map((a:any)=>`
          <tr><td>${a.antibiotic}</td><td>${a.mic||'—'}</td>
          <td style="font-weight:bold;color:${a.result==='SENSITIVE'||a.sensitivity==='S'?'#16a34a':a.result==='RESISTANT'||a.sensitivity==='R'?'#dc2626':'#d97706'}">${a.result||a.sensitivity}</td></tr>`).join('')}
        </tbody></table>`:'' }
      ${c.clinical_notes||c.finalReport||c.final_report?`<h3 class="section-title">Remarks / Clinical Notes</h3><p class="data-val">${c.clinical_notes||c.finalReport||c.final_report}</p>`:''}
    `;
    await openTemplatedPrintWindow({
      api, contentHtml, docTitle:'Culture & Sensitivity Report', reportType:'CULTURE', department:'Microbiology',
      barcodeText:c.request_number||c.id, noteText:'This report is generated by the Laboratory Information System.',
    });
  };

  return(
    <Layout title="Lab Reports — Culture & Templates" actions={
      <div className="flex gap-2">
        <button onClick={()=>setSection('culture')} className={`btn-secondary text-sm ${section==='culture'?'bg-[#02357A] text-white border-[#015CA6]':''}`}>🦠 Culture & Sensitivity</button>
        <button onClick={()=>setSection('templates')} className={`btn-secondary text-sm ${section==='templates'?'bg-[#02357A] text-white border-[#015CA6]':''}`}>📋 Report Templates</button>
      </div>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      {section==='culture'&&(
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Microbiology culture growth, organism identification and antibiotic sensitivity results</p>
            <button onClick={()=>{setCForm({...defaultCForm});initAbx();setCulturePatient(null);setShowForm(true);}} className="btn-primary">🦠 New Culture Report</button>
          </div>
          <div className="card overflow-hidden">
            <div className="p-3 bg-slate-50 border-b grid grid-cols-7 text-xs font-semibold text-slate-500 uppercase gap-2">
              <span className="col-span-2">Patient</span><span>Specimen</span><span>Collected</span><span>Growth</span><span>Organism</span><span>Actions</span>
            </div>
            {loading?<div className="p-8 text-center text-slate-400">Loading...</div>
            :cultures.length===0?<div className="p-8 text-center text-slate-400">No culture reports yet. Click "+ New Culture Report" to add one.</div>
            :cultures.map((c:any)=>(
              <div key={c.id} className="grid grid-cols-7 gap-2 p-3 items-center hover:bg-slate-50 border-b text-sm">
                <div className="col-span-2"><p className="font-semibold">{c.patient_first_name} {c.patient_last_name}</p><p className="text-xs font-mono text-[#02357A]">{c.uhid}</p></div>
                <span>{c.specimen_type||c.specimenType}</span>
                <span className="text-xs text-slate-400">{c.collection_date||c.collectionDate}</span>
                <Badge label={(c.growth_status||c.growthStatus||'AWAITING').replace('_',' ')} className={GROWTH_COLORS[c.growth_status||c.growthStatus||'AWAITING']}/>
                <span className="text-xs truncate">{c.organism_identified||c.organismIdentified||'—'}</span>
                <div className="flex gap-1">
                  <button onClick={()=>setSelectedCulture(c)} className="text-xs text-[#02357A] hover:underline">View</button>
                  <button onClick={()=>printCulture(c)} className="text-xs text-slate-500 hover:underline">🖨️</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section==='templates'&&(
        <div>
          <p className="text-sm text-slate-500 mb-4">Standard lab panel templates — defines which parameters are included in each test panel</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {REPORT_TEMPLATES.map(tmpl=>(
              <div key={tmpl.id} className="card p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-slate-800 text-sm">{tmpl.name}</h3>
                  <span className="text-xs bg-blue-50 text-[#02357A] px-2 py-0.5 rounded font-mono">{tmpl.id}</span>
                </div>
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded mb-3 inline-block">{tmpl.category}</span>
                <div className="flex flex-wrap gap-1">
                  {tmpl.params.map(p=><span key={p} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{p}</span>)}
                </div>
                <p className="text-xs text-slate-400 mt-2">{tmpl.params.length} parameters auto-load when ordered</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Culture Form Panel */}
      <SlidePanel isOpen={showForm} onClose={()=>setShowForm(false)} title="New Culture & Sensitivity Report" icon="🦠" size="xl"
        actions={<button onClick={saveCulture} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save Culture Report'}</button>}>
        <div className="space-y-4">
          <PatientSearchInput label="Patient *" selected={culturePatient}
            onSelect={(p:any)=>{setCulturePatient(p);setCForm(f=>({...f,patientId:p.id}));}}
            onClear={()=>{setCulturePatient(null);setCForm(f=>({...f,patientId:''}));}}/>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Specimen Type *</label>
              <select className="input" value={cForm.specimenType} onChange={e=>setCForm({...cForm,specimenType:e.target.value})}>
                {SPECIMEN_TYPES.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="label">Collection Date</label><input type="date" className="input" value={cForm.collectionDate} onChange={e=>setCForm({...cForm,collectionDate:e.target.value})}/></div>
            <div><label className="label">Gram Stain Result</label><input className="input" value={cForm.gramStain} onChange={e=>setCForm({...cForm,gramStain:e.target.value})} placeholder="e.g. Gram-positive cocci in clusters"/></div>
            <div><label className="label">Incubation (hours)</label><input type="number" className="input" value={cForm.incubationHours} onChange={e=>setCForm({...cForm,incubationHours:e.target.value})}/></div>
          </div>
          <div>
            <label className="label">Growth Status *</label>
            <div className="grid grid-cols-5 gap-2">
              {['NO_GROWTH','GROWTH_PRESENT','CONTAMINATED','AWAITING','MIXED_GROWTH'].map(g=>(
                <button key={g} type="button" onClick={()=>setCForm({...cForm,growthStatus:g})}
                  className={`py-2 px-2 rounded-lg text-xs font-semibold border-2 ${cForm.growthStatus===g?'border-[#015CA6] bg-blue-50 text-[#02357A]':'border-slate-200 text-slate-600'}`}>
                  {g.replace(/_/g,' ')}
                </button>
              ))}
            </div>
          </div>
          {cForm.growthStatus==='GROWTH_PRESENT'&&(
            <>
              <div><label className="label">Organism Identified</label>
                <select className="input" value={cForm.organismIdentified} onChange={e=>setCForm({...cForm,organismIdentified:e.target.value})}>
                  <option value="">— Select from master / type below —</option>
                  {organisms.filter((o:any)=>o.is_common).length>0&&<optgroup label="⭐ Common">
                    {organisms.filter((o:any)=>o.is_common).map((o:any)=><option key={o.organism_code} value={o.organism_name}>{o.organism_name}</option>)}
                  </optgroup>}
                  {organisms.filter((o:any)=>!o.is_common).length>0&&<optgroup label="All Organisms">
                    {organisms.filter((o:any)=>!o.is_common).map((o:any)=><option key={o.organism_code} value={o.organism_name}>{o.organism_name}</option>)}
                  </optgroup>}
                </select>
                <input className="input mt-1 text-xs" value={cForm.organismIdentified} onChange={e=>setCForm({...cForm,organismIdentified:e.target.value})} placeholder="Or type custom organism name..."/>
              </div>
              <div><label className="label">Colony Count</label><input className="input" value={cForm.colonyCount} onChange={e=>setCForm({...cForm,colonyCount:e.target.value})} placeholder="e.g. >10^5, Mixed, Insignificant"/></div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label mb-0">Antibiotic Sensitivity</label>
                  <button type="button" onClick={()=>setShowAllAbx(!showAllAbx)} className="text-xs text-[#02357A] hover:underline">{showAllAbx?'Hide untested':'Show all'}</button>
                </div>
                <div className="border rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 text-xs font-semibold text-slate-500 uppercase bg-slate-50 p-2 border-b">
                    <span className="col-span-4">Antibiotic</span><span className="col-span-2">MIC</span><span className="col-span-6">Sensitivity</span>
                  </div>
                  <div className="divide-y max-h-64 overflow-y-auto">
                    {ANTIBIOTIC_GROUPS.map(g=>(
                      <React.Fragment key={g.group}>
                        <div className="bg-slate-50 px-3 py-1 text-xs font-bold text-slate-500 uppercase">{g.group}</div>
                        {g.drugs.map(drug=>{
                          const row=abxRows.find(r=>r.antibiotic===drug)||{antibiotic:drug,sensitivity:'NT',mic:''};
                          if(!showAllAbx&&row.sensitivity==='NT'&&!row.mic)return null;
                          return(
                            <div key={drug} className="grid grid-cols-12 gap-2 p-2 items-center">
                              <span className="col-span-4 text-xs text-slate-700">{drug}</span>
                              <input className="col-span-2 input text-xs py-1" value={row.mic||''} onChange={e=>setAbxRows(rows=>rows.map(r=>r.antibiotic===drug?{...r,mic:e.target.value}:r))} placeholder="MIC"/>
                              <div className="col-span-6 flex gap-1">
                                {SENSITIVITY.map(s=>(
                                  <button key={s} type="button"
                                    onClick={()=>setAbxRows(rows=>rows.map(r=>r.antibiotic===drug?{...r,sensitivity:s}:r))}
                                    className={`px-2 py-1 rounded text-xs font-bold flex-1 ${row.sensitivity===s?SEN_COLORS[s]+' text-white':'bg-slate-100 text-slate-500'}`}>{s}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }).filter(Boolean)}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 mt-1 text-xs">{Object.entries(SEN_LABELS).map(([k,v])=><span key={k} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${SEN_COLORS[k]}`}/>{v}</span>)}</div>
              </div>
            </>
          )}
          <div><label className="label">Final Report / Interpretation</label><textarea className="input w-full" rows={3} value={cForm.finalReport} onChange={e=>setCForm({...cForm,finalReport:e.target.value})} placeholder="Pathologist's final interpretation..."/></div>
          <div><label className="label">Clinical Notes</label><input className="input w-full" value={cForm.clinicalNotes} onChange={e=>setCForm({...cForm,clinicalNotes:e.target.value})} placeholder="Antibiotic recommendations..."/></div>
        </div>
      </SlidePanel>

      {/* View Culture */}
      <SlidePanel isOpen={!!selectedCulture} onClose={()=>setSelectedCulture(null)} title={`Culture — ${selectedCulture?.specimen_type||''}`} icon="🦠" size="lg">
        {selectedCulture&&(
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {[['Patient',`${selectedCulture.patient_first_name||''} ${selectedCulture.patient_last_name||''}`],['UHID',selectedCulture.uhid||'—'],['Specimen',selectedCulture.specimen_type],['Growth Status',(selectedCulture.growth_status||'').replace('_',' ')]].map(([l,v])=>(
                <div key={String(l)} className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">{l}</p><p className="font-medium">{v}</p></div>
              ))}
            </div>
            {selectedCulture.gram_stain&&<div className="bg-blue-50 rounded p-2"><p className="text-xs text-blue-500">Gram Stain</p><p>{selectedCulture.gram_stain}</p></div>}
            {selectedCulture.organism_identified&&<div className="bg-red-50 border border-red-200 rounded p-2"><p className="text-xs text-red-500">Organism</p><p className="font-bold text-red-700">{selectedCulture.organism_identified}</p></div>}
            {(selectedCulture.antibiotic_results||selectedCulture.antibiotics||[]).length>0&&(
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-slate-50"><th className="p-2 text-left border">Antibiotic</th><th className="p-2 border">MIC</th><th className="p-2 border">Result</th></tr></thead>
                <tbody>{(selectedCulture.antibiotic_results||selectedCulture.antibiotics||[]).map((a:any,i:number)=>(
                  <tr key={i} className="border-b"><td className="p-2 border">{a.antibiotic}</td><td className="p-2 border text-center">{a.mic||'—'}</td><td className="p-2 border text-center"><span className={`px-2 py-0.5 rounded font-bold text-white text-xs ${SEN_COLORS[a.sensitivity||a.result]||'bg-slate-400'}`}>{a.sensitivity||a.result}</span></td></tr>
                ))}</tbody>
              </table>
            )}
            {selectedCulture.final_report&&<div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-400 mb-1">Report</p><p>{selectedCulture.final_report}</p></div>}
            <button onClick={()=>printCulture(selectedCulture)} className="btn-primary w-full">🖨️ Print Culture Report</button>
          </div>
        )}
      </SlidePanel>
    </Layout>
  );
}
